import { ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './FormStepper.module.scss';
import { StepDefinition } from './types';

interface FormStepperProps {
  steps: StepDefinition[];
  onSubmit: () => Promise<void> | void;
  isSubmitting?: boolean;
  errorMessage?: ReactNode;
  submitDisabled?: boolean;
  /** Overrides the last step's button text. Defaults to the generic t('submit'). */
  submitLabel?: string;
  /** Overrides the back-link text. Defaults to the generic t('backToPrevPage'). */
  backLabel?: string;
  /**
   * Makes the back link active on the FIRST step too, for a flow that has a screen before the
   * stepper (e.g. an intro). When omitted, step 0 renders an empty, disabled link as before.
   */
  onFirstStepBack?: () => void;
  /** Text of the first step's back link. Only used when onFirstStepBack is provided. */
  firstStepBackLabel?: string;
  /**
   * Width of the back link. The default 110px fits the generic "חזרה לדף הקודם"; a flow with a
   * longer label can widen it so the text does not wrap onto two lines.
   */
  backLinkWidthClass?: string;
  /** Called when the visible step changes (including initial mount). */
  onStepChange?: (stepIndex: number, analyticsStepId?: string) => void;
  /** Resume anonymous early-lead drafts on the saved step. */
  initialStepIndex?: number;
  /** Rendered at the top of the scrollable step area (e.g. expanded early-lead capture). */
  scrollHeader?: ReactNode;
}

const FormStepper = ({
  steps,
  onSubmit,
  isSubmitting = false,
  errorMessage,
  submitDisabled = false,
  submitLabel,
  backLabel,
  onFirstStepBack,
  firstStepBackLabel,
  backLinkWidthClass = 'w-[110px]',
  onStepChange,
  initialStepIndex = 0,
  scrollHeader,
}: FormStepperProps) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(() => Math.min(Math.max(initialStepIndex, 0), Math.max(steps.length - 1, 0)));
  const articleRef = useRef<HTMLElement>(null);

  // Fire only when the step itself changes. Callers build `steps` and `onStepChange` inline, so they get a new
  // identity on every render; depending on them re-fired the callback on each render, and a callback that
  // saves state (the open-fund early-lead autosave) then looped until React gave up with error #185 (WOR-1394).
  const latestRef = useRef({ steps, onStepChange });
  latestRef.current = { steps, onStepChange };

  useEffect(() => {
    const { steps: currentSteps, onStepChange: notify } = latestRef.current;
    notify?.(step, currentSteps[step]?.analyticsStepId);
  }, [step]);

  const scrollToTop = () => {
    articleRef.current?.scrollIntoView({ block: 'start', behavior: 'instant' });
  };

  const handleBack = () => {
    if (isSubmitting) return;
    if (step === 0) {
      // Only a flow that supplied onFirstStepBack has anywhere to go from the first step.
      onFirstStepBack?.();
      return;
    }
    setStep(step - 1);
    scrollToTop();
  };

  const handleNext = async () => {
    if (isSubmitting) return;

    const currentStep = steps[step];
    if (currentStep?.validate) {
      const isValid = await currentStep.validate();
      if (!isValid) return;
    }

    if (step === steps.length - 1) {
      if (submitDisabled) return;
      await onSubmit();
      return;
    }

    setStep(step + 1);
    scrollToTop();
  };

  const isLastStep = step === steps.length - 1;
  const nextLabel = isSubmitting ? t('submitting') : isLastStep ? (submitLabel ?? t('submit')) : t('continue');

  return (
    <div className={`${styles.root} w-full`}>
      <article
        ref={articleRef}
        data-form-scroll-root
        className={`${styles.stepScroll} flex-1 min-h-0 flex flex-col items-center w-full overflow-x-hidden overflow-y-auto`}
      >
        {scrollHeader}
        <div className={`${styles.stepContent} flex justify-center w-full`}>{steps[step]?.content}</div>
      </article>
      <footer className={`${styles.footer} w-full mt-4 max-md:mt-2 shrink-0`}>
        <div className={styles.errorSlot} role="status" aria-live="polite" aria-atomic="true">
          {errorMessage ? (
            <p className="body-md-regular text-(--status-rejected-text) bg-(--status-rejected-bg) border border-(--status-rejected-border) rounded-lg px-3 py-2">
              {errorMessage}
            </p>
          ) : null}
        </div>
        {/* מחוון השלבים. בדסקטופ פסים ברוחב מלא; במסך צר קווים קצרים
            ממורכזים, כמו .req-dot במקור (HAT-1170). */}
        <div className={`${styles.dots} flex gap-7 mx-8 max-md:mx-4 max-md:gap-3`}>
          {Array.from({ length: steps.length }).map((_, index) => (
            <div
              key={index}
              className={`${styles.dot} ${index <= step ? styles.dotActive : ''} w-full h-[6px] rounded-full ${index <= step ? 'bg-(--surface-primary)' : 'bg-(--border-main)'}`}
            ></div>
          ))}
        </div>
        <div
          className={`${styles.actions} flex justify-between items-center px-8 py-4 max-md:px-4 max-md:pt-3 max-md:pb-[max(0.75rem,env(safe-area-inset-bottom))]`}
        >
          <button
            type="button"
            className={`${styles.backLink} body-md-semibold text-(--font-body-1) ${backLinkWidthClass} max-md:w-auto`}
            disabled={isSubmitting || (step === 0 && !onFirstStepBack)}
            onClick={handleBack}
          >
            {step > 0 ? (backLabel ?? t('backToPrevPage')) : onFirstStepBack ? (firstStepBackLabel ?? backLabel ?? t('backToPrevPage')) : ''}
          </button>

          {/* המונה "2/4" — אין לו מקבילה במקור; במסך צר המחוון לבדו אומר את זה. */}
          <h4 className={styles.counter}>
            <span className="heading-h3-semibold text-(--font-primary)">{step + 1}</span>
            <span className="heading-h4-regular text-(--font-body-3)">/{steps.length}</span>
          </h4>
          <button type="button" className={`body-lr-semibold ${styles.button}`} onClick={handleNext} disabled={isSubmitting || (isLastStep && submitDisabled)}>
            <span>{nextLabel}</span>
            {!isLastStep && (
              <span className={styles.chevron} aria-hidden="true">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
            )}
          </button>
        </div>
      </footer>
    </div>
  );
};

export default FormStepper;
